import { PermissionType } from '@/enums/PermissionType';
import { AssistantService } from '@/api/services/assistant.service';
import {
  MENU_IMPORT_TARGET,
  PLANS_TARGET,
} from '@/domain/assistant/assistant-nav-targets';
import type { TAssistantKind } from '@/interfaces/assistant.interface';
import type {
  IGhCalloutCard,
  IHostToolDefinition,
} from '../../gpthelp-host-contract';
import { activityContextFor } from '../../assistant/assistant-activity';
import {
  activityActionTitle,
  changesListCard,
  mergeActivity,
} from '../../cards/activity-cards';
import type { TActivityEntry } from '../../cards/activity-cards';
import { ofKind } from '../../cards/card-kinds';
import { fitText } from '../../cards/card-text';
import { toolCardId } from '../../cards/card-ids';
import {
  editorFitsTask,
  MENU_TASKS,
  menuImportCard,
  NAV_EDITORS,
  NAV_TASKS,
  navHandoffCard,
  navLockedCard,
  navOpenCard,
} from '../../cards/nav-cards';
import type { INavCardInput, TNavTask } from '../../cards/nav-cards';
import { MENU_PAGES } from '../../assistant/assistant-navigate';
import { HostToolError } from '../tool-types';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpTool,
  IGpthelpToolGate,
} from '../tool-types';
import { assistantCanToggle } from '../tool-gate';
import {
  getLimitArg,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalNumberArray,
  getRequiredString,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensureAnyPermissionOrThrow } from '../tool-scope';
import { FOLLOW_UP_MAX_LENGTH, toolResultEnvelope } from '../tool-envelope';
import { canOpenPage, DISPLAY_PARAM } from '../tool-display';
import { menuProposeTool } from './menu-propose-tool';
import { stockProposeTool } from './stock-propose-tool';
import { supportTools } from './support-tools';

const NAV_PURPOSES = ['open', 'handoff'] as const;
const NAV_REASONS = ['irreversible', 'money', 'legal', 'notSupported'] as const;
/** Option groups are deleted on the Opciones page. */
const OPTIONS_PAGE = 'menu-options';
const MAX_HIGHLIGHT_ITEMS = 50;

/** Nothing navigates until the user taps the card (plan §9.7). */
const NAV_OPEN_NOTE =
  "The page is NOT open: the user opens it with the card's button. Never say it opened or that you opened it. One short sentence at most; do not repeat the card.";
/** The plan lock card without a button: this user cannot see or change the plan. */
const NAV_PLAN_LOCKED_NOTE =
  "The card says the store's plan does not include this page; it has no button. Never mention a button or opening the page. One short sentence at most; do not repeat the card.";
/** The role lock card: this user's role cannot do the task; it has no page button. */
const NAV_ROLE_LOCKED_NOTE =
  "This user's role cannot do this; the card says so, who to ask and, when it applies, offers hiding instead. It has no page button: never tell the user to open the page or tap a button for it. One short sentence at most; do not repeat the card.";
/** Tasks only the Menú does: the card opens it, whatever page was asked. */
const MENU_ONLY_TASKS: readonly TNavTask[] = [
  'deleteProduct',
  'deleteCategory',
  'productPhoto',
  'allergens',
];
/** Tasks that need the catalog permission (translations have their own). */
const CATALOG_EDIT_TASKS: readonly TNavTask[] = [
  'deleteProduct',
  'deleteCategory',
  'deleteOptionGroup',
  'productPhoto',
  'allergens',
  'vat',
  'printing',
];

function navigateDefinition(pageKeys: string[] | null): IHostToolDefinition {
  return {
    name: 'ui_navigate',
    description:
      "Shows the user a card with a button that opens an admin page; it never navigates by itself. purpose 'open': the user wants to go to or see a page. purpose 'handoff': the user asked for something you do not do (deleting, photos, translations, allergens, VAT, printers, payments, cash registers, invoicing, settings, users): they do it on that page; give the reason and the task when one fits (the card lists the steps). Before a Menu handoff about a product or category, find it with the menu tools and pass itemId, or categoryId with editor 'category', so the card opens its editor. Pages the store's plan lacks show the plan instead. 'menu-import' = load or update the menu from photos; 'store-settings' = order types, opening hours and payment methods; 'plans' = the plan; 'user-account' = the user's own PIN. No page changes a password: the user logs out and uses 'Forgot my password'; show no card for it.",
    sideEffects: 'none',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        page: pageKeys
          ? { type: 'string', enum: pageKeys }
          : { type: 'string' },
        categoryId: {
          type: 'integer',
          description:
            'Menu pages only: open this category (e.g. the one to delete).',
        },
        itemId: {
          type: 'integer',
          description: "Menu pages only: opens this product's editor.",
        },
        editor: {
          type: 'string',
          enum: [...NAV_EDITORS],
          description:
            "Menu pages only: 'category' opens categoryId's editor; 'newProduct' starts a new product in categoryId; 'newCategory' starts a new category.",
        },
        highlightItemIds: {
          type: 'array',
          items: { type: 'integer' },
          maxItems: MAX_HIGHLIGHT_ITEMS,
          description: 'Menu page only: products to flash when it opens.',
        },
        purpose: { type: 'string', enum: [...NAV_PURPOSES] },
        reason: {
          type: 'string',
          enum: [...NAV_REASONS],
          description:
            'handoff only. irreversible: deleting or anything that cannot be undone; money: payments, cash registers, refunds; legal: invoicing; notSupported: anything else.',
        },
        task: {
          type: 'string',
          enum: [...NAV_TASKS],
          description:
            "handoff only: the card lists the steps for this task. deleteProduct, deleteCategory, deleteOptionGroup = delete it; productPhoto, allergens, vat = of one product; translations = of a product or category; printing = which printers print a product's or category's orders; storeSettings = order types, opening hours, payment methods; team = change a person's role or branches (adding a person has no task).",
        },
      },
      required: ['page', 'purpose'],
    },
  };
}

function highlightArg(args: Record<string, unknown>): number[] {
  const raw = args.highlightItemIds;
  if (Array.isArray(raw) && raw.length === 0) return [];
  const ids = getOptionalNumberArray(
    args,
    'highlightItemIds',
    MAX_HIGHLIGHT_ITEMS
  );
  return (ids ?? []).filter((id) => Number.isInteger(id) && id > 0);
}

/** A Menú category of this store, by id; null when it is not one. */
function menuCategory(
  deps: ICartadigitalAdminHostToolDeps,
  categoryId: number | null
): { id: number; name: string } | null {
  if (categoryId === null) return null;
  const category = deps.menuStore.categoryMap?.[categoryId];
  return category ? { id: category.id, name: category.name } : null;
}

/**
 * The menu to show a category of: one of the store's other menus, else null
 * (the base menu, or a menu the store no longer offers).
 */
function menuOfCategory(
  deps: ICartadigitalAdminHostToolDeps,
  categoryId: number
): number | null {
  const menuId = deps.menuStore.categoryMap?.[categoryId]?.menuId;
  if (typeof menuId !== 'number') return null;
  const offered = deps.menuStore.availableMenus?.some(
    (menu) => menu.id === menuId
  );
  return offered ? menuId : null;
}

/**
 * What a Menú card opens, in this order: a product's editor, a category's
 * editor, a new product in a category, a new category, else a jump to the
 * category. Ids that are not this store's are dropped, and so is an editor
 * the task is not done in (its steps would not match the screen).
 */
function menuLinkOf(
  deps: ICartadigitalAdminHostToolDeps,
  args: Record<string, unknown>,
  task: TNavTask | null
): Pick<INavCardInput, 'item' | 'category' | 'editor' | 'menuId'> {
  const itemId = getOptionalNumber(args, 'itemId');
  const requestedEditor = getOptionalEnum(args, 'editor', NAV_EDITORS);
  const editor =
    requestedEditor && editorFitsTask(requestedEditor, task)
      ? requestedEditor
      : null;
  const category = menuCategory(deps, getOptionalNumber(args, 'categoryId'));
  const item = itemId !== null ? deps.menuStore.itemMap?.[itemId] : undefined;

  if (item)
    return {
      item: { id: item.id, name: item.name },
      category: null,
      editor: null,
      menuId: menuOfCategory(deps, item.categoryId),
    };
  if (editor === 'newCategory')
    return { item: null, category: null, editor, menuId: null };
  return {
    item: null,
    category,
    editor: category ? editor : null,
    menuId: category ? menuOfCategory(deps, category.id) : null,
  };
}

/**
 * The page a card opens: a task done on one page only opens that page,
 * whatever page was asked (option groups on Opciones; products and
 * categories on the Menú).
 */
function taskPageKey(
  requested: string,
  task: TNavTask | null,
  gate: IGpthelpToolGate
): string {
  const offered = (key: string) =>
    gate.navTargets.some((entry) => entry.key === key);
  if (task === 'deleteOptionGroup' && offered(OPTIONS_PAGE))
    return OPTIONS_PAGE;
  if (task && MENU_ONLY_TASKS.includes(task) && !MENU_PAGES.includes(requested))
    return MENU_PAGES.find(offered) ?? requested;
  return requested;
}

/**
 * Whether the task's steps fit the page the card opens: the Menú's on the
 * Menú, Opciones' on Opciones (e.g. no product steps under a button that
 * opens the store's invoicing settings).
 */
function stepsFitPage(task: TNavTask, pageKey: string): boolean {
  if (MENU_TASKS.includes(task)) return MENU_PAGES.includes(pageKey);
  if (task === 'deleteOptionGroup') return pageKey === OPTIONS_PAGE;
  return true;
}

/** The Menú link of a page that is not the Menú: nothing to open in it. */
const NO_MENU_LINK: Pick<
  INavCardInput,
  'item' | 'category' | 'editor' | 'menuId'
> = { item: null, category: null, editor: null, menuId: null };

/**
 * What the member could hide instead of deleting it (hiding can be
 * undone): the product the card opens or the one it flashes, else the
 * category it opens. Nothing for several products, or when the assistant
 * cannot prepare it.
 */
function hideInsteadName(
  deps: ICartadigitalAdminHostToolDeps,
  gate: IGpthelpToolGate,
  input: Pick<INavCardInput, 'item' | 'category' | 'highlightItemIds'>
): string | null {
  if (!assistantCanToggle(gate)) return null;
  if (input.item) return input.item.name;
  const [itemId, ...others] = input.highlightItemIds;
  if (itemId === undefined) return input.category?.name ?? null;
  if (others.length) return null;
  return deps.menuStore.itemMap?.[itemId]?.name ?? null;
}

/**
 * The name of an action's applied count for the model: products for a
 * change of the menu, changes for an inventory change (plan P5-3: its rows
 * are stock items and recipes, never products).
 */
const COUNT_FIELDS: Readonly<Record<TAssistantKind, 'products' | 'changes'>> = {
  'menu.changes': 'products',
  'stock.changes': 'changes',
};

/** What the model reads about one change; the card shows it to the user. */
function changeData(entry: TActivityEntry, title: string) {
  if (entry.kind === 'photo') {
    const { update } = entry;
    return {
      kind: 'photoUpdate',
      title,
      user: update.user?.name ?? null,
      at: update.publishedAt,
      counts: update.counts,
      undone: update.undone,
      canUndo: update.canUndo,
    };
  }
  const { item } = entry;
  return {
    kind: 'assistant',
    title,
    status: item.status,
    user: item.user.name,
    at: item.appliedAt,
    [ofKind(COUNT_FIELDS, item.kind)]: item.appliedCount,
    undoneAt: item.undoneAt,
    undoneBy: item.undoneBy?.name ?? null,
    canUndo: item.canUndo,
    undoUntil: item.undoUntil,
  };
}

/**
 * The assistant's own tools, offered first (plan F11: the proposal tools
 * lead, the menu's then the inventory's; the support handoff right after
 * ui_navigate).
 */
export const assistantTools: IGpthelpTool[] = [
  menuProposeTool,
  stockProposeTool,
  {
    definition: navigateDefinition(null),
    definitionFor: (gate) =>
      navigateDefinition(gate.navTargets.map((target) => target.key)),
    policy: 'ui',
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.navTargets.length > 0,
    execute(args, { deps, gate, turn, fmt }) {
      const task = getOptionalEnum(args, 'task', NAV_TASKS);
      const pageKey = taskPageKey(getRequiredString(args, 'page'), task, gate);
      const target = gate.navTargets.find((entry) => entry.key === pageKey);
      if (!target) {
        throw new HostToolError('INVALID_ARGS', 'Unknown page', {
          page: pageKey,
          allowed: gate.navTargets.map((entry) => entry.key),
        });
      }
      const purpose = getOptionalEnum(args, 'purpose', NAV_PURPOSES) ?? 'open';
      const reason = getOptionalEnum(args, 'reason', NAV_REASONS);
      const onMenu = MENU_PAGES.includes(target.key);
      const input: INavCardInput = {
        id: toolCardId('nav', turn.requestId),
        target,
        pageTitle: fmt.t(target.titleKey),
        groupTitle: target.groupTitleKey ? fmt.t(target.groupTitleKey) : null,
        ...(onMenu ? menuLinkOf(deps, args, task) : NO_MENU_LINK),
        highlightItemIds: onMenu ? highlightArg(args) : [],
        task: task && stepsFitPage(task, target.key) ? task : null,
      };
      // Without the catalog permission the Menú's editors have no delete
      // button and nothing to save: the lock card says so, without steps or
      // a button to the page.
      const catalogLocked =
        input.task !== null &&
        CATALOG_EDIT_TASKS.includes(input.task) &&
        !gate.canManageCatalog;
      const followUps: string[] = [];

      let shown: 'menuImport' | 'locked' | 'handoff' | 'open';
      let card: IGhCalloutCard;
      // What the model may say: a card without a page button must not be
      // described as one the user opens.
      let note = NAV_OPEN_NOTE;
      if (target.key === MENU_IMPORT_TARGET) {
        shown = 'menuImport';
        card = menuImportCard(input.id, gate.multipleMenus, fmt);
      } else if (target.locked) {
        shown = 'locked';
        const canSeePlans = canOpenPage(gate, PLANS_TARGET);
        card = navLockedCard(input, canSeePlans, fmt);
        if (!canSeePlans) note = NAV_PLAN_LOCKED_NOTE;
      } else if (purpose === 'handoff' && catalogLocked) {
        shown = 'locked';
        note = NAV_ROLE_LOCKED_NOTE;
        const hideName =
          reason === 'irreversible' ? hideInsteadName(deps, gate, input) : null;
        card = navHandoffCard(
          { ...input, reason, hideName, catalogLocked },
          fmt
        );
        if (hideName)
          followUps.push(
            fitText(
              fmt,
              'assistant.nav.hideChip',
              { name: hideName },
              'name',
              FOLLOW_UP_MAX_LENGTH
            )
          );
      } else if (purpose === 'handoff') {
        shown = 'handoff';
        // Deleting cannot be undone; hiding the product or category can.
        const hideName =
          reason === 'irreversible' ? hideInsteadName(deps, gate, input) : null;
        card = navHandoffCard(
          { ...input, reason, hideName, catalogLocked },
          fmt
        );
        if (hideName)
          followUps.push(
            fitText(
              fmt,
              'assistant.nav.hideChip',
              { name: hideName },
              'name',
              FOLLOW_UP_MAX_LENGTH
            )
          );
      } else {
        shown = 'open';
        card = navOpenCard(input, fmt);
      }

      return toolResultEnvelope({
        data: { shown, page: target.key, note },
        cards: [card],
        followUps,
      });
    },
  },
  ...supportTools,
  {
    definition: {
      name: 'assistant_listChanges',
      description:
        'The latest changes the assistant made in this store (and menu updates from photos), newest first: what, who, when, and whether they can still be undone. To undo one, call it with display:true so the user taps the undo button on the card; you never undo anything yourself.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          limit: { type: 'integer', minimum: 1, maximum: 5, default: 3 },
          display: DISPLAY_PARAM,
        },
      },
    },
    policy: 'read',
    // Independent of writes: undo stays reachable while writes are off.
    isAvailable: (gate) =>
      gate.hasRestaurantScope &&
      (gate.canManageCatalog || gate.canDisableCatalog),
    async execute(args, { deps, scope, turn, fmt }) {
      ensureAnyPermissionOrThrow({
        deps,
        permissions: [
          PermissionType.CatalogManage,
          PermissionType.CatalogDisable,
        ],
        message: 'No tiene permisos para ver los cambios del menú',
      });
      const limit = getLimitArg(args, { def: 3, max: 5 });
      const response = await runToolApiCall(() =>
        AssistantService.listActions(scope.restaurantId, { limit })
      );
      const entries = mergeActivity(response).slice(0, limit);
      const data = {
        changes: entries.map((entry) =>
          changeData(
            entry,
            entry.kind === 'action'
              ? activityActionTitle(entry.item, fmt)
              : fmt.t('assistant.activity.photo')
          )
        ),
        more: !!response.nextCursor,
      };
      if (!getOptionalBoolean(args, 'display')) return data;

      return toolResultEnvelope({
        data,
        cards: [
          changesListCard(
            toolCardId('changes', turn.requestId),
            response,
            limit,
            activityContextFor(deps, scope),
            fmt
          ),
        ],
      });
    },
  },
];
